import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Crown, Users, Calendar, Settings, ChevronRight, 
  Award, TrendingUp, Clock, Gift, Edit, Check, X,
  AlertCircle, Search, Download, RefreshCw, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function FoundingMembersTab() {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeSection, setActiveSection] = useState('overview');
  const [editingConfig, setEditingConfig] = useState(false);
  const [configForm, setConfigForm] = useState({});
  const [migrating, setMigrating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, statsRes, tiersRes, logsRes] = await Promise.all([
        api.get('/admin/founding-members/config'),
        api.get('/admin/founding-members/stats'),
        api.get('/admin/loyalty-tiers/'),
        api.get('/admin/loyalty-tiers/audit-logs', { params: { limit: 20 } })
      ]);

      setConfig(configRes.data);
      setConfigForm(configRes.data);
      setStats(statsRes.data);
      setTiers(tiersRes.data.tiers || []);
      setAuditLogs(logsRes.data.logs || []);
    } catch (error) {
      console.error('Error fetching founding members data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      const res = await api.put('/admin/founding-members/config', configForm);
      setConfig(res.data.config);
      setEditingConfig(false);
      toast.success('Configuration saved');
    } catch (error) {
      toast.error('Error saving configuration');
    }
  };

  const migrateExistingSignups = async () => {
    if (!confirm('This will tag all existing campaign and loyalty signups as Founding Members. Continue?')) {
      return;
    }

    setMigrating(true);
    try {
      const res = await api.post('/admin/founding-members/migrate-existing-signups', { reason: 'Initial founding member migration' });
      const data = res.data;
      toast.success(`Migration complete! ${(data.campaign_signups_updated || 0) + (data.loyalty_signups_updated || 0)} members tagged.`);
      fetchData();
    } catch (error) {
      console.error('Migration error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log out and log back in, then try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Migration failed - please try again');
      }
    } finally {
      setMigrating(false);
    }
  };

  const searchCustomer = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const res = await api.get(`/founding-members/customer/${encodeURIComponent(searchQuery)}/status`);
      setSearchResults([res.data]);
    } catch (error) {
      setSearchResults([]);
      toast.error('Customer not found');
    } finally {
      setSearching(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getCampaignStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      pre_launch: 'bg-purple-100 text-purple-800',
      active: 'bg-green-100 text-green-800',
      expired: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Crown className="w-6 h-6 text-amber-500" />
            Founding Members & Loyalty Tiers
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage the founding member campaign and lifetime rewards tiers
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 border-b dark:border-gray-700 pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: TrendingUp },
          { id: 'config', label: 'Configuration', icon: Settings },
          { id: 'tiers', label: 'Lifetime Tiers', icon: Award },
          { id: 'lookup', label: 'Customer Lookup', icon: Search },
          { id: 'audit', label: 'Audit Logs', icon: Shield }
        ].map((section) => (
          <Button
            key={section.id}
            variant={activeSection === section.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveSection(section.id)}
            className={activeSection === section.id ? 'bg-amber-600 hover:bg-amber-700' : ''}
          >
            <section.icon className="w-4 h-4 mr-2" />
            {section.label}
          </Button>
        ))}
      </div>

      {/* Overview Section */}
      {activeSection === 'overview' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Total Founding Members</p>
                  <p className="text-3xl font-bold mt-1">{stats?.total_founding_members || 0}</p>
                </div>
                <Crown className="w-10 h-10 text-amber-200" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-5 border dark:border-gray-700"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">With Coupon Codes</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
                    {stats?.founding_members_with_codes || 0}
                  </p>
                </div>
                <Gift className="w-10 h-10 text-purple-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-5 border dark:border-gray-700"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Campaign Status</p>
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${getCampaignStatusColor(stats?.campaign_status)}`}>
                    {stats?.campaign_status?.replace('_', ' ').toUpperCase() || 'PRE LAUNCH'}
                  </span>
                </div>
                <Clock className="w-10 h-10 text-blue-500" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-5 border dark:border-gray-700"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Launch Discount Active</p>
                  <p className={`text-2xl font-bold mt-1 ${stats?.founding_discount_active ? 'text-green-600' : 'text-gray-400'}`}>
                    {stats?.founding_discount_active ? 'YES' : 'NO'}
                  </p>
                </div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${stats?.founding_discount_active ? 'bg-green-100' : 'bg-gray-100'}`}>
                  {stats?.founding_discount_active ? <Check className="w-6 h-6 text-green-600" /> : <X className="w-6 h-6 text-gray-400" />}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Campaign Breakdown */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Founding Members by Campaign
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {(stats?.campaign_breakdown || [
                { campaign_id: 1, founding_members: 0 },
                { campaign_id: 2, founding_members: 0 },
                { campaign_id: 3, founding_members: 0 },
                { campaign_id: 4, founding_members: 0 }
              ]).map((campaign) => (
                <div key={campaign.campaign_id} className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Campaign {campaign.campaign_id}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{campaign.founding_members}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Key Dates */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Key Dates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Calendar className="w-8 h-8 text-amber-500" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Grand Opening Date</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {formatDate(stats?.grand_opening_date)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <Clock className="w-8 h-8 text-red-500" />
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Founding Discount Ends</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {formatDate(stats?.founding_discount_end_at)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Migration Tool - ALWAYS VISIBLE */}
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6 border border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-amber-600 mt-1" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200">
                  Tag Existing Signups as Founding Members
                </h3>
                <p className="text-amber-700 dark:text-amber-300 mt-1">
                  Run this migration to tag all existing campaign and loyalty signups as Founding Members. 
                  This should only be run once before the grand opening.
                </p>
                <Button
                  onClick={migrateExistingSignups}
                  disabled={migrating}
                  className="mt-4 bg-amber-600 hover:bg-amber-700"
                  data-testid="migrate-signups-btn"
                >
                  {migrating ? 'Migrating...' : 'Migrate Existing Signups'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Section */}
      {activeSection === 'config' && config && (
        <div className="space-y-6">
          {/* Info Box */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800 dark:text-amber-200">How Founding Member Discounts Work</h4>
                <ul className="mt-2 text-sm text-amber-700 dark:text-amber-300 space-y-1">
                  <li>• <strong>During the 30-day window:</strong> All Founding Members get 10% off every order</li>
                  <li>• <strong>After 30 days:</strong> Everyone (including Founding Members) follows tier-based discounts</li>
                  <li>• Tier discounts: Bronze (1,000+ FJD) 10% | Silver (2,000+ FJD) 12.5% | Gold (3,000+ FJD) 15% | Platinum (5,000+ FJD) 20%</li>
                  <li>• If a Founding Member's tier discount is higher than 10%, they get the higher one even during the window</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Grand Opening & Founding Member Configuration
              </h3>
              {!editingConfig ? (
                <Button onClick={() => setEditingConfig(true)} variant="outline" size="sm">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button onClick={saveConfig} size="sm" className="bg-green-600 hover:bg-green-700">
                    <Check className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                  <Button onClick={() => { setEditingConfig(false); setConfigForm(config); }} variant="outline" size="sm">
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="grand_opening_date">Grand Opening Date</Label>
                  <Input
                    id="grand_opening_date"
                    type="date"
                    value={configForm.grand_opening_date || ''}
                    onChange={(e) => setConfigForm({ ...configForm, grand_opening_date: e.target.value })}
                    disabled={!editingConfig}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty until you're ready to announce</p>
                </div>

                <div>
                  <Label htmlFor="founding_discount_percentage">Founding Member Discount (%)</Label>
                  <Input
                    id="founding_discount_percentage"
                    type="number"
                    value={configForm.founding_discount_percentage || 10}
                    onChange={(e) => setConfigForm({ ...configForm, founding_discount_percentage: parseFloat(e.target.value) })}
                    disabled={!editingConfig}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="founding_campaign_status">Campaign Status</Label>
                  <select
                    id="founding_campaign_status"
                    value={configForm.founding_campaign_status || 'pre_launch'}
                    onChange={(e) => setConfigForm({ ...configForm, founding_campaign_status: e.target.value })}
                    disabled={!editingConfig}
                    className="mt-1 w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
                  >
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="pre_launch">Pre-Launch (Accepting Founding Members)</option>
                    <option value="active">Active (Grand Opening Started)</option>
                    <option value="expired">Expired (30-Day Window Ended)</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="founding_campaign_public_mode">Public Landing Page Mode</Label>
                  <select
                    id="founding_campaign_public_mode"
                    value={configForm.founding_campaign_public_mode || 'pre_launch_offer'}
                    onChange={(e) => setConfigForm({ ...configForm, founding_campaign_public_mode: e.target.value })}
                    disabled={!editingConfig}
                    className="mt-1 w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
                  >
                    <option value="pre_launch_offer">Pre-Launch Offer (Show Founding Member 10% Offer)</option>
                    <option value="post_launch_rewards">Post-Launch (Show Standard Rewards Program)</option>
                    <option value="redirect">Redirect to Main Rewards Page</option>
                    <option value="archived">Archived (Show Page Unavailable)</option>
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="founding_discount_start_at">Discount Start Date</Label>
                  <Input
                    id="founding_discount_start_at"
                    type="date"
                    value={configForm.founding_discount_start_at?.split('T')[0] || ''}
                    onChange={(e) => {
                      const startDate = e.target.value;
                      // Auto-calculate end date as 30 days from start
                      const endDate = startDate 
                        ? new Date(new Date(startDate).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                        : '';
                      setConfigForm({ 
                        ...configForm, 
                        founding_discount_start_at: startDate ? `${startDate}T00:00:00+12:00` : '',
                        founding_discount_end_at: endDate ? `${endDate}T23:59:59+12:00` : configForm.founding_discount_end_at
                      });
                    }}
                    disabled={!editingConfig}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">When Founding Members start receiving 10% discount</p>
                </div>

                <div>
                  <Label htmlFor="founding_discount_end_at">Discount End Date (30-day window)</Label>
                  <Input
                    id="founding_discount_end_at"
                    type="date"
                    value={configForm.founding_discount_end_at?.split('T')[0] || ''}
                    onChange={(e) => setConfigForm({ 
                      ...configForm, 
                      founding_discount_end_at: e.target.value ? `${e.target.value}T23:59:59+12:00` : ''
                    })}
                    disabled={!editingConfig}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-set to 30 days after start when you set the start date</p>
                </div>

                {/* Discount Window Status */}
                {configForm.founding_discount_start_at && configForm.founding_discount_end_at && (
                  <div className={`p-4 rounded-lg ${
                    (() => {
                      const now = new Date();
                      const start = new Date(configForm.founding_discount_start_at);
                      const end = new Date(configForm.founding_discount_end_at);
                      if (now < start) return 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800';
                      if (now > end) return 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600';
                      return 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800';
                    })()
                  }`}>
                    <div className="flex items-center gap-2">
                      {(() => {
                        const now = new Date();
                        const start = new Date(configForm.founding_discount_start_at);
                        const end = new Date(configForm.founding_discount_end_at);
                        if (now < start) {
                          const daysUntil = Math.ceil((start - now) / (1000 * 60 * 60 * 24));
                          return (
                            <>
                              <Clock className="w-5 h-5 text-blue-600" />
                              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                Discount window starts in {daysUntil} day{daysUntil !== 1 ? 's' : ''}
                              </span>
                            </>
                          );
                        }
                        if (now > end) {
                          return (
                            <>
                              <X className="w-5 h-5 text-gray-500" />
                              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                Discount window has ended - Tier-based discounts now apply
                              </span>
                            </>
                          );
                        }
                        const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
                        return (
                          <>
                            <Check className="w-5 h-5 text-green-600" />
                            <span className="text-sm font-medium text-green-700 dark:text-green-300">
                              ACTIVE - {daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining in founding member discount window
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 mt-4">
                  <input
                    type="checkbox"
                    id="auto_transition"
                    checked={configForm.founding_campaign_auto_transition_enabled !== false}
                    onChange={(e) => setConfigForm({ ...configForm, founding_campaign_auto_transition_enabled: e.target.checked })}
                    disabled={!editingConfig}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="auto_transition" className="text-sm">
                    Auto-transition landing pages when grand opening starts
                  </Label>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="extension_allowed"
                    checked={configForm.founding_campaign_extension_allowed !== false}
                    onChange={(e) => setConfigForm({ ...configForm, founding_campaign_extension_allowed: e.target.checked })}
                    disabled={!editingConfig}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="extension_allowed" className="text-sm">
                    Allow extending founding member campaign
                  </Label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tiers Section */}
      {activeSection === 'tiers' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Lifetime Rewards Tiers
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Customers earn lifetime tier status based on their total eligible spend. Discounts do not stack.
            </p>

            <div className="space-y-4">
              {tiers.map((tier, index) => (
                <motion.div
                  key={tier.tier_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-4 rounded-lg border dark:border-gray-600"
                  style={{ borderLeftColor: tier.badge_color, borderLeftWidth: '4px' }}
                >
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${tier.badge_color}20` }}>
                    <Award className="w-6 h-6" style={{ color: tier.badge_color }} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 dark:text-white">{tier.tier_name}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Unlock at FJ${tier.min_spend_threshold.toLocaleString()} lifetime spend
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold" style={{ color: tier.badge_color }}>
                      {tier.discount_percentage}%
                    </p>
                    <p className="text-xs text-gray-500">discount for life</p>
                  </div>
                  {tier.free_coffee_per_day > 0 && (
                    <div className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                      + Free Coffee Daily
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">How Tiers Work:</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <li>• Founding Member 10% discount is separate from tier discounts</li>
                <li>• During the 30-day launch window, the best discount applies (Founding OR Tier)</li>
                <li>• After the launch window, only tier discounts apply</li>
                <li>• Tier status is permanent once earned</li>
                <li>• Platinum members get one free eligible coffee per day</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Customer Lookup Section */}
      {activeSection === 'lookup' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Customer Loyalty Lookup
            </h3>
            
            <div className="flex gap-2 mb-6">
              <Input
                placeholder="Search by email, phone, or customer ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchCustomer()}
                className="flex-1"
              />
              <Button onClick={searchCustomer} disabled={searching}>
                <Search className="w-4 h-4 mr-2" />
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </div>

            {searchResults.length > 0 && (
              <div className="space-y-4">
                {searchResults.map((customer) => (
                  <div key={customer.customer_id} className="p-4 border dark:border-gray-600 rounded-lg">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{customer.name}</h4>
                        <p className="text-sm text-gray-500">{customer.email} | {customer.phone}</p>
                      </div>
                      {customer.is_founding_member && (
                        <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium flex items-center gap-1">
                          <Crown className="w-4 h-4" />
                          Founding Member
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Lifetime Spend</p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          FJ${customer.lifetime_eligible_spend?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Current Tier</p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {customer.current_tier?.tier_name || 'No Tier Yet'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Current Discount</p>
                        <p className="font-semibold text-green-600">
                          {customer.current_discount?.percentage}% ({customer.current_discount?.source})
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Next Tier</p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {customer.next_tier ? `${customer.next_tier.tier_name} (FJ$${customer.next_tier.spend_remaining?.toFixed(2)} to go)` : 'Max Tier Reached'}
                        </p>
                      </div>
                    </div>

                    {customer.is_founding_member && (
                      <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                        <p className="text-sm text-amber-800 dark:text-amber-200">
                          <strong>Founding Member Status:</strong> {customer.founding_member_status?.toUpperCase()} 
                          {customer.founding_discount_days_remaining !== null && ` (${customer.founding_discount_days_remaining} days remaining)`}
                        </p>
                      </div>
                    )}

                    {/* Progress Bar */}
                    {customer.next_tier && (
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>{customer.current_tier?.tier_name || 'Start'}</span>
                          <span>{customer.next_tier.tier_name}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div
                            className="bg-amber-500 h-2 rounded-full transition-all"
                            style={{ width: `${customer.tier_progress_percentage}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit Logs Section */}
      {activeSection === 'audit' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Audit Logs
            </h3>

            {auditLogs.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                No audit logs yet
              </p>
            ) : (
              <div className="space-y-2">
                {auditLogs.map((log) => (
                  <div key={log.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {log.action.replace(/_/g, ' ').toUpperCase()}
                      </span>
                      <span className="text-gray-500 text-xs">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                      By: {log.admin_user} {log.reason && `- ${log.reason}`}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
